// Right-aligned heading metadata. Replaces the assortment of
// "refreshes every 5m" placeholders that told the operator the polling
// interval (useless) instead of when the underlying data was last
// refreshed (what they actually need to know).
//
// Pass `lastRefreshed` as the timestamp of the most recent data point
// the card is showing — typically MAX(some_timestamp_column) from the
// underlying table (sc_pull's checked_at, ga4_pull's pulled_at,
// index-check's last_checked, etc).

/**
 * Right-aligned heading metadata. Renders "Updated 5m ago · next in 2h"
 * with the full ISO timestamp on hover.
 *
 * @param cron — schedule of the producer for this card. Supported:
 *   "@daily-at-NN-utc" → fires once daily at NN:00 UTC
 *   "@every-NN-min"    → fires every NN minutes
 *   "@event-driven"    → no schedule (e.g. sitemap submit on deploy)
 *   omit → no "next in" rendered
 */
export function HeaderMeta({
  lastRefreshed,
  fallback = "never",
  prefix = "Updated",
  extra,
  cron,
}: {
  lastRefreshed: string | null | undefined;
  fallback?: string;
  prefix?: string;
  extra?: string;
  cron?: string;
}) {
  const nextStr = cron ? nextFireFromCron(cron) : null;
  if (!lastRefreshed) {
    return (
      <span className="text-xs text-ink-400" title={cron ? `Next scheduled run: ${nextStr ?? "n/a"}` : undefined}>
        {fallback}
        {nextStr ? ` · next ${nextStr}` : ""}
      </span>
    );
  }
  const ago = relTime(lastRefreshed);
  const full = new Date(lastRefreshed).toLocaleString();
  return (
    <span className="text-xs text-ink-500" title={full}>
      {prefix} {ago}
      {nextStr ? ` · next ${nextStr}` : ""}
      {extra ? ` · ${extra}` : ""}
    </span>
  );
}

function relTime(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return "just now";
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 30) return `${d}d ago`;
  const mo = Math.floor(d / 30);
  return `${mo}mo ago`;
}

/**
 * Compute the next-fire time for the small set of cron schedules our
 * cards use. Kept inline (no cron library) since we only have two
 * shapes and a parser would be ~10x the code.
 */
function nextFireFromCron(cron: string): string | null {
  if (cron === "@event-driven") return "on next deploy";
  // "@daily-at-08-utc" → next 08:00 UTC.
  const dailyMatch = cron.match(/^@daily-at-(\d{1,2})-utc$/);
  if (dailyMatch) {
    const hour = parseInt(dailyMatch[1]!, 10);
    const now = new Date();
    const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), hour, 0, 0));
    if (next.getTime() <= now.getTime()) next.setUTCDate(next.getUTCDate() + 1);
    return `in ${forwardTime(next)}`;
  }
  // "@every-15-min" → next quarter-hour boundary.
  const everyMatch = cron.match(/^@every-(\d+)-min$/);
  if (everyMatch) {
    const m = parseInt(everyMatch[1]!, 10);
    const now = new Date();
    const minute = now.getMinutes();
    const nextMinute = Math.ceil((minute + 1) / m) * m;
    const next = new Date(now);
    next.setMinutes(nextMinute, 0, 0);
    if (next.getTime() <= now.getTime()) next.setMinutes(next.getMinutes() + m, 0, 0);
    return `in ${forwardTime(next)}`;
  }
  return null;
}

function forwardTime(future: Date): string {
  const ms = future.getTime() - Date.now();
  if (ms < 60_000) return `${Math.max(0, Math.floor(ms / 1000))}s`;
  const min = Math.floor(ms / 60_000);
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  const remMin = min % 60;
  if (hr < 24) return remMin > 0 ? `${hr}h ${remMin}m` : `${hr}h`;
  const d = Math.floor(hr / 24);
  return `${d}d`;
}
